import React, { useEffect, useMemo, useState } from "react";

import { ArrayUtils, incomplete, queryIsLoading, useGetAllCarbonPrograms, useGetCarbonProgramContractsForPrograms, useUpdatePartner } from "utils";

import { Partner } from "models";

import { AdminSection, Button, Col, Drawer, Form, Input, Option, PageSpin, Row, Select, Typography, Message as message } from "components";

interface PartnerSettingsProps {
  onUpdateSuccess: () => void;
  partner: Partner;
}

type PartnerWithSplitAddresses = {
  addressStreet: string;
  addressNumber: string;
  addressCity: string;
  zip: string;
};

export const PartnerSettings = ({ partner, onUpdateSuccess }: PartnerSettingsProps) => {
  const [showDeleteConfirmDrawer, setShowDeleteConfirmDrawer] = useState(false);
  const [form] = Form.useForm();
  const updatePartnerMutation = useUpdatePartner();
  const allCarbonProgramsQuery = useGetAllCarbonPrograms();
  const contractQueries = useGetCarbonProgramContractsForPrograms(allCarbonProgramsQuery.data?.map((cp) => cp.id) ?? [], {
    enabled: allCarbonProgramsQuery.isFetched,
  });

  // Form.Item's initialValue is only applied once, on first field registration (AntD does not
  // re-apply it on prop changes), so a save-triggered refetch needs this to push fresh values in.
  useEffect(() => {
    form.setFieldsValue({
      name: partner.name,
      disabled: !!partner.disabled,
      autoAssignableCarbonProgram: partner.autoAssignableCarbonProgram,
      contactName: partner.contactName,
      contactEmail: partner.contactEmail,
      contactPhone: partner.contactPhone,
      addressStreet: partner.invoiceAddress?.split(", ")[0],
      addressNumber: partner.invoiceAddress?.split(", ")[1],
      addressCity: partner.invoiceCity?.split(", ")[1],
      zip: partner.invoiceCity?.split(", ")[0],
      invoiceCountry: partner.invoiceCountry,
    });
  }, [partner, form]);

  // Map of programId => { hasContract }
  const programIdToHasContract = useMemo((): Record<string, { hasContract: boolean }> => {
    if (contractQueries.some(queryIsLoading)) return {};
    return ArrayUtils.indexBy(
      contractQueries.map((listOfContracts) => ({ programId: listOfContracts.data?.[0]?.programId ?? "", hasContract: !!listOfContracts.data?.[0] })),
      ({ programId }) => programId
    );
  }, [contractQueries]);

  const complete = (v: PartnerWithSplitAddresses) => {
    const invoiceAddress = `${v.addressStreet}, ${v.addressNumber}`;
    const invoiceCity = `${v.zip}, ${v.addressCity}`;

    updatePartnerMutation.mutate(
      {
        ...v,
        invoiceAddress,
        invoiceCity,
        id: partner.id,
      },
      {
        onSuccess: () => {
          message({
            content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
            icon: "gremlin",
          });
          onUpdateSuccess();
        },
      }
    );
  };

  const handleDisablePartnerModalOpen = (disabled: boolean) => {
    if (!disabled) {
      return;
    }
    setShowDeleteConfirmDrawer(true);
  };

  return updatePartnerMutation.isPending || !partner ? (
    <PageSpin />
  ) : (
    <>
      <Form className="midi-form" form={form} name="confirm" onFinish={(v) => complete(v)} onFinishFailed={() => incomplete()}>
        <AdminSection headline="General details" />
        <Row>
          <Col sm={20} md={20}>
            <Input name="name" label="Name" rules={[{ required: true }]} initialValue={partner.name} />
          </Col>
          <Col sm={4} md={4}>
            <Select name="disabled" label="Active" rules={[{ required: true }]} initialValue={!!partner.disabled} onChange={handleDisablePartnerModalOpen}>
              <Option value={false}>Yes</Option>
              <Option value={true}>No</Option>
            </Select>
          </Col>
        </Row>
        <Row>
          <Col sm={24}>
            <Select
              name="autoAssignableCarbonProgram"
              label="Default Carbon Program"
              rules={[{ required: false }]}
              initialValue={partner.autoAssignableCarbonProgram}
              showSearch
            >
              {allCarbonProgramsQuery.data?.map((cp) => {
                const programMissingContract = !programIdToHasContract[cp.id]?.hasContract && !cp.noProgramTerms;
                return (
                  <Option key={cp.id} value={cp.id} disabled={programMissingContract}>
                    {cp.programName}
                    {programMissingContract && " (missing contract)"}
                  </Option>
                );
              })}
            </Select>
          </Col>
        </Row>

        <AdminSection headline="Point prices" />
        <Typography variant="paragraph-m">Check the partner's associated carbon program for point price details</Typography>

        <AdminSection headline="Contact info" />
        <Row>
          <Col>
            <Input name="contactName" label="Contact person" rules={[{ required: false }]} initialValue={partner.contactName} />
          </Col>
        </Row>
        <Row>
          <Col sm={24} md={12}>
            <Input name="contactEmail" label="Contact email" rules={[{ required: false }]} initialValue={partner.contactEmail} />
          </Col>
          <Col sm={24} md={12}>
            <Input name="contactPhone" label="Contact phone" rules={[{ required: false }]} initialValue={partner.contactPhone} />
          </Col>
        </Row>

        <AdminSection headline="Invoicing info" />
        <Row>
          <Col sm={24} md={18}>
            <Input name="addressStreet" label="Street" rules={[{ required: true }]} initialValue={partner.invoiceAddress?.split(", ")[0]} />
          </Col>
          <Col sm={24} md={6}>
            <Input name="addressNumber" label="Street Number" rules={[{ required: true }]} initialValue={partner.invoiceAddress?.split(", ")[1]} />
          </Col>
        </Row>

        <Row>
          <Col sm={24} md={18}>
            <Input name="addressCity" label="City" rules={[{ required: true }]} initialValue={partner.invoiceCity?.split(", ")[1]} />
          </Col>
          <Col sm={24} md={6}>
            <Input name="zip" label="Post Code" rules={[{ required: true }]} initialValue={partner.invoiceCity?.split(", ")[0]} />
          </Col>
        </Row>

        <Row>
          <Col sm={24} md={24}>
            <Input name="invoiceCountry" label="Country" rules={[{ required: true }]} initialValue={partner.invoiceCountry} />
          </Col>
        </Row>

        <Row>
          <Col>
            <Form.Item>
              <Button type="primary" htmlType="submit" disabled={updatePartnerMutation.isPending} loading={updatePartnerMutation.isPending}>
                Save
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Drawer
        footer={<Button onClick={() => setShowDeleteConfirmDrawer(false)}>Yes, I understand</Button>}
        placement="right"
        title="Disable partner"
        visible={showDeleteConfirmDrawer}
        onClose={() => setShowDeleteConfirmDrawer(false)}
      >
        <Typography variant="paragraph-m">If you disable {partner.name}, all farmers who have been assigned to this partner will be unassigned.</Typography>
        <Typography variant="paragraph-m">All assigned farmers switch will back to their default hectare limit and carbon price.</Typography>
        <Typography variant="paragraph-m">You must manually remove any assigned credits if required.</Typography>
      </Drawer>
    </>
  );
};
